import { Col, Form, message, Row, Spin } from 'antd';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getPatients, getReservation } from '../../services/reservation';
import { getCookie } from './../../services/cookies';
import { getCurrency, getAreas, getJobPositions } from './../../services/general';
import DisclaimerEdition from './components/DisclaimerEdition';
import GeneralData from './components/GeneralData';
import Header from './components/Header';
import PatientsAndServices from './components/PatientsAndServices';
import PatientsWithForecast from './components/PatientsWithForecast';
import ServicesSummary from './components/ServicesSummary';
import ValidationButtons from './components/ValidationButtons';
import countrySettings from './countrySettings.json';
import useForecasts from './hooks/useForecasts';
import usePaymentMethods from './hooks/usePaymentMethods';
import usePaymentAmount from '../../hooks/usePaymentAmount';

const ReservationSettingsPage = () => {
  const [loading, setLoading] = useState({
    reservation: false,
    patients: false,
    currency: false,
  });
  const [reservation, setReservation] = useState();
  const [patients, setPatients] = useState();
  const [branchOfficeId, setBranchOfficeId] = useState(getCookie('branch_office_id'));
  const [currency, setCurrency] = useState();
  const [refreshForm, setRefreshForm] = useState([]);
  const [totalPatients, setTotalPatients] = useState();
  const [form] = Form.useForm();
  const [disablePatientInput, setDisablePatientInput] = useState(false);
  const [summaryItems, setSummaryItems] = useState();
  const [summaryTotal, setSummaryTotal] = useState();
  const [companies, setCompanies] = useState([]);
  const [disabled, setDisabled] = useState({ patientBlocking: false })
  const [projects, setProjects] = useState([]);
  const [areasList, setAreasList] = useState([]);
  const [jobPositionsList, setJobPositionsList] = useState([]);
  const countryCode = getCookie('country_code');
  const { id } = useParams();
  const { setCompany, ...paymentMethods } = usePaymentMethods();
  const forecastData = useForecasts(id, null)
  const { paymentAmount, isLoading: isLoadingPaymentAmount } = usePaymentAmount(reservation);
  const location = useLocation();
  const currentPage = location.pathname
  const companyId = Form.useWatch('company_id', form);

  const fetchReservation = useCallback(async () => {
    setLoading((loading) => {
      return { ...loading, reservation: true };
    });
    try {
      const response = await getReservation({ reservation_id: id });
      if (response?.length > 0) {
        setReservation(response[0]);
        setTotalPatients(response[0].total_patients);
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Error al obtener la reserva',
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
    } finally {
      setLoading((loading) => {
        return { ...loading, reservation: false };
      });
    }
  }, [id]);

  const fetchPatients = useCallback(async () => {
    setLoading((loading) => {
      return { ...loading, patients: true };
    });
    try {
      const response = await getPatients({ reservation_id: id });
      response?.filter((patient) => patient.project_id)?.forEach((patient) => {
        setProjects((_projects) => [..._projects, { id: patient.project_id, name: patient.project_name }]);
      })
      setPatients(response);
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Error al obtener los pacientes',
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
    } finally {
      setLoading((loading) => {
        return ({ ...loading, patients: false });
      });
    }
  }, [id]);

  const groupByService = useCallback(() => {
    const prestationObj = {};
    let totalValue = 0;

    patients.forEach((item) => {
      item.prestations.forEach((prestation) => {
        let prestationAmount = parseFloat(prestation.sale_amount) || 0;

        if (countrySettings.applyConditionalPricing[countryCode]) {
          if (prestation.removedExamIds?.length > 0) {
            const removedExams = (prestation.battery_exams || []).filter(e => prestation.removedExamIds.includes(e.id));
            let removedExamsTotal = 0;
            for (const e of removedExams) {
              removedExamsTotal += parseFloat(e.net_amount !== undefined ? e.net_amount : e.sale_amount) || 0;
            }
            prestationAmount = Math.max(0, prestationAmount - removedExamsTotal);
          }

          if (prestation.additionals?.length > 0) {
            let additionalsTotal = 0;
            for (const item of prestation.additionals) {
              additionalsTotal += parseFloat(item.net_amount !== undefined ? item.net_amount : item.sale_amount) || 0;
            }
            prestationAmount += additionalsTotal;
          }
        }

        if (!prestationObj[prestation.name]) {
          prestationObj[prestation.name] = {
            id: `${prestation.id}-${prestation.type}`,
            unit_price: prestationAmount,
            sale_amount: 0,
            quantity: 0,
          };
        }

        const oldPrestation = prestationObj[prestation.name];
        prestationObj[prestation.name] = {
          ...oldPrestation,
          sale_amount: parseFloat(oldPrestation.sale_amount) + prestationAmount,
          quantity: oldPrestation.quantity + 1,
        };
        totalValue += prestationAmount;
      });
    });

    const prestationArray = Object.keys(prestationObj).map((prestationName) => {
      return {
        ...prestationObj[prestationName],
        name: prestationName,
      };
    });

    const locateCode = currency?.locateCode || 'es-CL';
    const currencyCode = currency?.currencyCode || 'CLP';
    setSummaryItems(prestationArray);
    setSummaryTotal({ totalValue, locateCode, currencyCode });

  }, [patients, currency?.locateCode, currency?.currencyCode, countryCode]);

  useEffect(() => {
    fetchReservation();
    fetchPatients();
  }, [fetchPatients, fetchReservation]);

  useEffect(() => {
    if (companyId) {
      getAreas({ limit: 100, company_id: companyId })
        .then(res => setAreasList(res || []))
        .catch(error => {
          console.error("Error fetching areas:", error);
          message.error("Error al obtener áreas");
        });
      getJobPositions({ limit: 100, company_id: companyId })
        .then(res => setJobPositionsList(res || []))
        .catch(error => {
          console.error("Error fetching job positions:", error);
          message.error("Error al obtener puestos de trabajo");
        });
    } else {
      setAreasList([]);
      setJobPositionsList([]);
    }
  }, [companyId]);

  const handleAreaCreated = useCallback((newArea) => {
    setAreasList(prev => [...prev, newArea]);
  }, []);

  const handleJobPositionCreated = useCallback((newJobPosition) => {
    setJobPositionsList(prev => [...prev, newJobPosition]);
  }, []);

  useEffect(() => {
    if (patients) groupByService();
  }, [groupByService, patients]);

  useEffect(() => {
    if (reservation?.company_id) {
      setCompany(reservation.company_id);
    }
  }, [reservation, setCompany]);

  const confirmRefreshPage = (event) => {
    event.preventDefault();
    const message = '¿Estás seguro que deseas abandonar?';
    return event.returnValue = message;
  };

  useEffect(() => {
    if (patients && totalPatients) {
      checkAdmittedPatients();
    }
    window.addEventListener('beforeunload', confirmRefreshPage);
    return () => {
      window.removeEventListener("beforeunload", confirmRefreshPage);
    };
  }, [patients, totalPatients]);

  const checkAdmittedPatients = () => {
    let _patients = patients;
    form.setFieldsValue({ 'total_patients': patients.length });
    const patientsWithoutServices = _patients.filter((item) => item['prestations'].length === 0);
    const patientsWithBlocks = _patients.filter((item) => item['blocks']);
    if (patientsWithoutServices.length === totalPatients || patientsWithBlocks === 0) {
      setDisablePatientInput(false);
    } else {
      setDisablePatientInput(true);
    }
  };
  const fetchCurrency = useCallback(async () => {
    try {
      setLoading(loading => { return { ...loading, currency: true } });
      const response = await getCurrency();
      setCurrency({ locateCode: response.locate_code, currencyCode: response.code })
    } catch (error) {
      console.log(error);
      message.error({
        content: `Error interno al buscar divisa`,
        style: {
          marginTop: '32vh',
          fontSize: 16
        }
      });
    } finally {
      setLoading(loading => { return { ...loading, currency: false } });
    }
  }, []);

  const patientsWithForecast = patients && forecastData && PatientsWithForecast({ patients, forecastData })

  return (
    <Row justify="center" className="reservation-settings-wrapper mb-80">
      <Col lg={24} xs={24}>
        {loading.reservation ? (
          <Row justify="center">
            <Spin className="cmt-spin margin-top" />
          </Row>
        ) : (
          reservation &&
          patients && (
            <Fragment>
              <Header
                loading={loading}
                setLoading={setLoading}
                reservation={reservation}
                forecastData={forecastData}
                patients={patientsWithForecast}
              />
              <GeneralData
                form={form}
                reservation={reservation}
                setReservation={setReservation}
                setTotalPatients={setTotalPatients}
                disablePatientInput={disablePatientInput}
                setRefreshForm={setRefreshForm}
                paymentData={paymentMethods.paymentData}
                setBranchOfficeId={setBranchOfficeId}
                sessionBranchOfficeId={branchOfficeId}
                fetchCurrency={fetchCurrency}
                companies={companies}
                setCompanies={setCompanies}
                countrySettings={countrySettings}
                countryCode={countryCode}
                paymentAmount={paymentAmount}
                isLoadingPaymentAmount={isLoadingPaymentAmount}
              />

              {patients.length ? (
                <Fragment>
                  <PatientsAndServices
                    form={form}
                    patients={patientsWithForecast}
                    setPatients={setPatients}
                    totalPatients={totalPatients}
                    setTotalPatients={setTotalPatients}
                    countryCode={countryCode}
                    currentPage={currentPage}
                    setDisabled={setDisabled}
                    countrySettings={countrySettings}
                    projects={projects}
                    setProjects={setProjects}
                    areasList={areasList}
                    jobPositionsList={jobPositionsList}
                    handleAreaCreated={handleAreaCreated}
                    handleJobPositionCreated={handleJobPositionCreated}
                  />
                  {countrySettings.showServicesSummary[countryCode] && (
                    <Col offset={1} lg={22} xs={22}>
                      <Spin className='cmt-spin' spinning={loading.currency}>
                        <ServicesSummary
                          summaryItems={summaryItems}
                          summaryTotal={summaryTotal}
                          currency={currency}
                        />
                      </Spin>
                    </Col>
                  )}
                </Fragment>
              ) : (
                <DisclaimerEdition reservation={reservation} />
              )}
              {form && (
                <ValidationButtons
                  form={form}
                  patients={patients}
                  summaryItems={summaryItems}
                  branchOfficeId={branchOfficeId}
                  reservation={reservation}
                  fetchReservation={fetchReservation}
                  paymentMethods={paymentMethods}
                  companies={companies}
                  countryCode={countryCode}
                  disabled={disabled}
                />
              )}
            </Fragment>
          )
        )}
      </Col>
    </Row>
  );
};

export default ReservationSettingsPage;
